import { useEffect, useState } from "react";
import { Plus, Save, Trash2, Loader2, Eye, EyeOff, MapPin, Briefcase, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";
import { listJobs, createJob, updateJob, deleteJob } from "@/lib/adminCmsApi";
import { errorToString } from "@/lib/errorUtils";

const EMPTY_JOB = {
  title: "",
  location: "",
  employment_type: "Full-time",
  summary: "",
  responsibilities: [],
  requirements: [],
  pay: "",
  is_open: true,
};

function ListField({ label, value, onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(value || []), v]);
    setDraft("");
  };
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/70 mb-2">{label}</label>
      <div className="space-y-2">
        {(value || []).map((it, i) => (
          <div key={i} className="flex items-start gap-2 group">
            <span className="mt-2 w-1 h-1 bg-[#0E2A57] rounded-full flex-shrink-0" />
            <input
              data-testid={`hiring-${label.toLowerCase()}-item-${i}`}
              className="inp flex-1"
              value={it}
              onChange={(e) => {
                const next = [...value];
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <button
              type="button"
              data-testid={`hiring-${label.toLowerCase()}-remove-${i}`}
              onClick={() => onChange(value.filter((_, x) => x !== i))}
              className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-800 px-2 py-2"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          data-testid={`hiring-${label.toLowerCase()}-new`}
          className="inp flex-1"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <button
          type="button"
          onClick={add}
          data-testid={`hiring-${label.toLowerCase()}-add`}
          className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
        >
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}

export default function AdminHiring() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // job object (existing or new draft)
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listJobs();
      setJobs(data || []);
    } catch (e) {
      toast.error("Failed to load job openings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onNew = () => setEditing({ ...EMPTY_JOB });

  const onSave = async () => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editing.id) {
        await updateJob(editing.id, editing);
        toast.success("Job opening updated");
      } else {
        await createJob(editing);
        toast.success("Job opening created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errorToString(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try {
      await deleteJob(job.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleOpen = async (job) => {
    try {
      await updateJob(job.id, { ...job, is_open: !job.is_open });
      toast.success(job.is_open ? "Position closed" : "Position re-opened");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <AdminShell title="Hiring · Job Openings">
      <style>{`
        .inp { background:#fff; border:1px solid rgba(14,42,87,0.18); padding:0.6rem 0.8rem; font-size:14px; width:100%; }
        .inp:focus { outline:none; border-color:#0E2A57; }
      `}</style>

      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-sm text-ags-text-muted max-w-xl">
            Manage open positions shown on the public <code className="font-mono text-xs bg-ags-cream px-1.5 py-0.5">/careers</code> page.
            Close a position to hide it without deleting.
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href="/careers"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[#0E2A57]/30 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]"
          >
            <ExternalLink size={12} /> View public page
          </a>
          <button
            onClick={onNew}
            data-testid="hiring-new-job"
            className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]"
          >
            <Plus size={14} /> New position
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#0E2A57]/15 p-12 flex items-center justify-center text-ags-text-muted">
          <Loader2 size={20} className="animate-spin mr-3" /> Loading…
        </div>
      ) : jobs.length === 0 && !editing ? (
        <div className="bg-white border border-[#0E2A57]/15 p-16 text-center">
          <Briefcase size={28} className="mx-auto text-[#0E2A57]/30 mb-4" />
          <div className="font-heading font-bold text-lg text-ags-ink">No job openings yet</div>
          <p className="text-sm text-ags-text-muted mt-2 mb-6">Create your first position to start receiving applications.</p>
          <button
            onClick={onNew}
            data-testid="hiring-new-job-empty"
            className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]"
          >
            <Plus size={14} /> Create first position
          </button>
        </div>
      ) : (
        <ul className="bg-white border border-[#0E2A57]/15 divide-y divide-ags-hairline">
          {jobs.map((j) => (
            <li key={j.id} data-testid={`hiring-row-${j.id}`} className="px-5 md:px-6 py-5 flex flex-wrap items-center gap-4 hover:bg-ags-cream">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-heading font-bold text-[15px] text-ags-ink tracking-[-0.02em] truncate">{j.title}</span>
                  {j.is_open ? (
                    <span className="bg-[#2BB6E5] text-ags-ink px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] font-medium">Open</span>
                  ) : (
                    <span className="bg-[#E5EBF2] text-ags-text-muted px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em]">Closed</span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-ags-text-muted">
                  {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {j.location}</span>}
                  {j.employment_type && <span>· {j.employment_type}</span>}
                  {j.pay && <span>· {j.pay}</span>}
                  <span>· /careers?job={j.slug}</span>
                </div>
              </div>
              <button
                onClick={() => toggleOpen(j)}
                data-testid={`hiring-toggle-${j.id}`}
                className="inline-flex items-center gap-1.5 border border-[#0E2A57]/25 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                {j.is_open ? <><EyeOff size={11} /> Close</> : <><Eye size={11} /> Re-open</>}
              </button>
              <button
                onClick={() => setEditing(j)}
                data-testid={`hiring-edit-${j.id}`}
                className="inline-flex items-center gap-1.5 bg-[#0E2A57] text-white hover:bg-[#050B14] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                Edit
              </button>
              <button
                onClick={() => onDelete(j)}
                data-testid={`hiring-delete-${j.id}`}
                className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 hover:bg-red-700 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                <Trash2 size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex" data-testid="hiring-editor">
          <div className="flex-1 bg-black/40" onClick={() => setEditing(null)} />
          <div className="w-full max-w-2xl bg-white shadow-2xl h-full overflow-y-auto">
            <div className="px-8 py-6 border-b border-ags-hairline flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">{editing.id ? "Edit position" : "New position"}</div>
                <h2 className="mt-1 font-heading font-extrabold text-2xl tracking-[-0.02em] text-ags-ink">{editing.title || "Untitled"}</h2>
              </div>
              <button
                onClick={() => setEditing(null)}
                data-testid="hiring-editor-close"
                className="text-ags-text-muted hover:text-ags-ink font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                Cancel
              </button>
            </div>

            <div className="px-8 py-6 space-y-5">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/70 mb-2">Job title *</label>
                <input
                  data-testid="hiring-input-title"
                  className="inp"
                  placeholder="Industrial Cleaning Specialist"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/70 mb-2">Location</label>
                  <input
                    data-testid="hiring-input-location"
                    className="inp"
                    placeholder="Dublin"
                    value={editing.location}
                    onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/70 mb-2">Employment type</label>
                  <select
                    data-testid="hiring-input-type"
                    className="inp"
                    value={editing.employment_type}
                    onChange={(e) => setEditing({ ...editing, employment_type: e.target.value })}
                  >
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Temporary</option>
                    <option>Apprenticeship</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/70 mb-2">Pay / Rate</label>
                <input
                  data-testid="hiring-input-pay"
                  className="inp"
                  placeholder="€14–€17 / hr · DOE"
                  value={editing.pay}
                  onChange={(e) => setEditing({ ...editing, pay: e.target.value })}
                />
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/70 mb-2">Summary</label>
                <textarea
                  data-testid="hiring-input-summary"
                  className="inp"
                  rows={4}
                  placeholder="Short description of the role."
                  value={editing.summary}
                  onChange={(e) => setEditing({ ...editing, summary: e.target.value })}
                />
              </div>

              <ListField
                label="Responsibilities"
                value={editing.responsibilities}
                onChange={(v) => setEditing({ ...editing, responsibilities: v })}
                placeholder="e.g. Operate floor scrubbers safely"
              />

              <ListField
                label="Requirements"
                value={editing.requirements}
                onChange={(v) => setEditing({ ...editing, requirements: v })}
                placeholder="e.g. Manual handling certificate"
              />

              <label className="flex items-center gap-3 cursor-pointer" data-testid="hiring-input-open">
                <input
                  type="checkbox"
                  checked={editing.is_open}
                  onChange={(e) => setEditing({ ...editing, is_open: e.target.checked })}
                  className="w-4 h-4 accent-[#0E2A57]"
                />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]">Position is open & visible to applicants</span>
              </label>
            </div>

            <div className="px-8 py-5 border-t border-ags-hairline flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted hover:text-ags-ink"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                data-testid="hiring-save"
                className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {editing.id ? "Save changes" : "Create position"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
